import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useData } from '../../context/DataContext';
import { useAuth } from '../../context/AuthContext';
import TeamMemberCard from './TeamMemberCard';
import TeamMemberModal from './TeamMemberModal';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiUsers } = FiIcons;

const TeamManagement = () => {
  const { teamMembers, leaves } = useData();
  const { hasPermission } = useAuth();
  
  const [showModal, setShowModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);

  // Access check
  if (!hasPermission('view_team') && !hasPermission('all')) {
    return (
      <div className="text-center py-12">
        <SafeIcon icon={FiUsers} className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Access Restricted</h3>
        <p className="text-gray-600">
          You don't have permission to view team management.
        </p>
      </div>
    );
  }

  const handleAddMember = () => {
    setSelectedMember(null);
    setShowModal(true);
  };

  const handleEditMember = (member) => {
    setSelectedMember(member);
    setShowModal(true);
  };

  // Enhance member data with availability
  const enhancedMembers = teamMembers.map(member => {
    const activeLeave = leaves.find(l => 
      l.userId === member.id && 
      l.status === 'approved' && 
      new Date(l.startDate) <= new Date() && 
      new Date(l.endDate) >= new Date()
    );
    return {
      ...member,
      availability: activeLeave ? 'on_leave' : 'available',
      leaveDetails: activeLeave
    };
  });

  const canManageTeam = hasPermission('all') || hasPermission('manage_team');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Team Management</h1>
          <p className="text-gray-600 mt-1">
            Manage your team members, track performance and availability
          </p>
        </div>
        <div className="flex space-x-3">
          {canManageTeam && (
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleAddMember}
              className="flex items-center space-x-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
            >
              <SafeIcon icon={FiPlus} className="w-5 h-5" />
              <span>Add Member</span>
            </motion.button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <AnimatePresence>
          {enhancedMembers.map((member, index) => (
            <motion.div
              key={member.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ delay: index * 0.1 }}
            >
              <div className="relative">
                <TeamMemberCard 
                  member={member} 
                  onEdit={() => handleEditMember(member)}
                  // Pass null for onDelete to hide quick-delete icon on card
                  // Deletion is now handled inside the edit modal for better flow
                  onDelete={null} 
                  canEdit={canManageTeam}
                />
                
                {member.availability === 'on_leave' && (
                  <div className="absolute top-4 right-14 bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded-full border border-orange-200 flex items-center shadow-sm">
                    <span className="w-2 h-2 bg-orange-500 rounded-full mr-1.5 animate-pulse"></span>
                    On Leave until {new Date(member.leaveDetails.endDate).toLocaleDateString()}
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {teamMembers.length === 0 && (
        <div className="text-center py-12">
          <SafeIcon icon={FiUsers} className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No team members</h3>
          <p className="text-gray-600">
            Start by adding your first team member.
          </p>
        </div>
      )}

      {/* Main Edit/Create Modal */}
      <AnimatePresence>
        {showModal && (
          <TeamMemberModal 
            member={selectedMember} 
            onClose={() => setShowModal(false)} 
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default TeamManagement;